import { useLayoutStore } from '../store/shared/layout-store';
import parsePhoneNumber from 'libphonenumber-js';
import { Timestamp } from 'firebase/firestore';
import { isToday, isYesterday } from './dates';
import { t } from '@/locales';

export const loadFile = (fileName) => {
	return window.location.origin + '/' + fileName;
};

export const fetchQueryParams = ({ pagination, filters }) => {
	const { page, limit: per_page } = pagination;
	let filterQuery = '';
	// @TODO: Handle different filter key types (arrays, object, ...)
	Object.keys(filters).forEach((key) => {
		filterQuery += `&${key}=${filters[key]}`;
	});
	// @TODO: Add sorting
	const query = `?page=${page}&per_page=${per_page}${filterQuery}`;
	return query;
};

/**
 * Normalize any date-ish value into a valid Date, or null when it can't be recovered.
 * Handles Firestore Timestamps, their JSON-serialized form
 * ({ type: 'firestore/timestamp/1.0', seconds, nanoseconds }), epoch numbers and strings.
 * Returns null instead of an Invalid Date so callers can render a placeholder
 * rather than throwing out of a render function.
 */
export const toValidDate = (value) => {
	if (value === null || value === undefined || value === '') return null;

	let date;
	if (value instanceof Date) {
		date = value;
	} else if (typeof value.toDate === 'function') {
		date = value.toDate();
	} else if (typeof value.seconds === 'number') {
		date = new Date(value.seconds * 1000);
	} else if (typeof value._seconds === 'number') {
		date = new Date(value._seconds * 1000);
	} else if (typeof value === 'number' || typeof value === 'string') {
		date = new Date(value);
	} else {
		return null;
	}

	return Number.isNaN(date.getTime()) ? null : date;
};

export const getDateObj = (date) => {
	if (date instanceof Timestamp) {
		return date.toDate();
	} else {
		if (date?.seconds) {
			return new Date(date.seconds * 1000);
		} else {
			return new Date(date);
		}
	}
};

export const dateTimeFormatter = (date) => {
	const locale = localStorage.getItem('lang') || 'en';
	if (!date) return '';
	const formatter = new Intl.DateTimeFormat(locale, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		hour12: false,
	});
	let dateObj = getDateObj(date);

	return formatter.format(dateObj);
};

export const dateFormatter = (
	date,
	options = { day: '2-digit', month: '2-digit', year: 'numeric' },
) => {
	const locale = localStorage.getItem('lang') || 'en';
	if (!date) return '';
	try {
		const formatter = new Intl.DateTimeFormat(locale, options);

		let dateObj = getDateObj(date);

		return formatter.format(dateObj);
	} catch (err) {
		console.error(err);
		return date || '--';
	}
};

export const timeFormatter = (date) => {
	const locale = localStorage.getItem('lang') || 'en';
	if (!date) return '';
	try {
		const formatter = new Intl.DateTimeFormat(locale, {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});
		let dateObj = getDateObj(date);
		return formatter.format(dateObj);
	} catch (err) {
		console.error(err);
		return date || '--';
	}
};

export const customDateFormatter = (date) => {
	if (!date) return '';

	date = getDateObj(date);

	if (isToday(date)) {
		return t('Today');
	}

	if (isYesterday(date)) {
		return t('Yesterday');
	}

	return dateFormatter(date, {
		day: '2-digit',
		weekday: 'short',
		month: 'short',
		year: 'numeric',
	});
};

export const commaFormatter = (num, options = {}) => {
	const { maximumFractionDigits } = options;

	if (isNaN(num)) return num;
	const formatter = new Intl.NumberFormat('en-US', {
		maximumFractionDigits: maximumFractionDigits,
	});
	return formatter.format(num);
};

export const priceFormatter = (num) => {
	if (isNaN(num)) return num;

	return parseFloat(num ?? 0).toLocaleString('en-US', {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
};

export const phoneNumberFormatter = (phone) => {
	if (!phone) return null;

	const phoneNumber = parsePhoneNumber(`+${String(phone).replace('+', '')}`);

	if (!phoneNumber) return null;

	return phoneNumber.formatInternational();
};

export const getLocalizedUrl = (url) => {
	const layoutStore = useLayoutStore();
	const locale = layoutStore.lang;
	return `/${locale}${url}`;
};

export const getDeepValue = (obj, keys) => {
	// If plain string, split it to array
	if (typeof keys === 'string') {
		keys = keys.split('.');
	}

	// Get key
	var key = keys.shift();

	// Get data for that key
	var keyData = obj[key];

	// Check if there is data
	if (!keyData) {
		return undefined;
	}

	// Check if we reached the end of query string
	if (keys.length === 0) {
		return keyData;
	}

	// recusrive call!
	return getDeepValue(Object.assign({}, keyData), keys);
};

export const getAPIDateFormat = (date) => {
	return moment(date).locale('en').format('YYYY-MM-DD');
};

export const getLocalizedValue = (obj, key) => {
	const layoutStore = useLayoutStore();
	const locale = layoutStore.lang[0].toUpperCase() + layoutStore.lang[1];
	return obj[`${key}${locale}`];
};

export function mergeRecursive(obj1, obj2) {
	for (const p in obj2) {
		try {
			// Property in destination object set; update its value.
			if (obj2[p].constructor == Object) {
				obj1[p] = mergeRecursive(obj1[p], obj2[p]);
			} else {
				obj1[p] = obj2[p];
			}
		} catch (e) {
			// Property in destination object not set; create it and set its value.
			obj1[p] = obj2[p];
		}
	}

	return obj1;
}

export const removeQueryParams = (url) => {
	if (!url) return '';

	return String(url).split('?')[0];
};
